import { useRoutine } from "@/hooks/useRoutines.ts";
import { getErrorStatus } from "@/shared/retryPolicy.ts";
import { RoutineDetail } from "@/views/Routines.tsx";
import { Zap } from "lucide-react";
import { useEffect } from "react";
import { CanvasModalCard } from "./CanvasModalCard.tsx";

/**
 * The canvas overlay for a routine target: the routines screen's own
 * {@link RoutineDetail}, in a card.
 *
 * It is the same component rather than a card-sized version of it, so a routine
 * opened from a thread has every capability it has on the settings screen and
 * neither copy can drift. The card supplies the title and the way back, so the
 * editor renders `embedded` and drops its in-tab header bar.
 *
 * The caller resolves the id from the canvas URN. A routine is addressed by ent
 * UUID (user) or slug (built-in) and `GET /api/a/routines/:id` resolves either,
 * so neither this nor the editor needs to know which kind it has.
 */
export const CanvasRoutineModal = ({
  routineId,
  container,
  showBack,
  isFullscreen,
  onBack,
  onClose,
  onUnloadable,
}: {
  /** The targeted routine's ent UUID or system slug. */
  routineId: string | undefined;
  /** The stack layer this modal portals into. */
  container: HTMLElement;
  /** Show the leading Back affordance (this layer sits above the stack root). */
  showBack: boolean;
  /** Whether the presented entity is shown fullscreen. */
  isFullscreen: boolean;
  /** Pop this layer. */
  onBack: () => void;
  /** Clear the whole stack. */
  onClose: () => void;
  /** Report that this layer's routine cannot be loaded, so the stack drops it. */
  onUnloadable?: () => void;
}) => {
  // Title only; `RoutineDetail` owns the body's own `useRoutine` (deduped).
  const { routine, error } = useRoutine(routineId);

  // A 404 means the routine is gone — deleted from the settings screen, or on
  // another device — and no amount of waiting brings it back. Report it so the
  // stack drops this layer rather than leaving a dead card the user cannot
  // clear: the stack is persisted, so the card returns on every visit.
  //
  // Deliberately only a 404, matching `CanvasArtifactModal`. A 5xx, a timeout,
  // or an offline client are transient, and discarding the user's canvas target
  // over a blip would lose real state.
  const isMissing = getErrorStatus(error) === 404;
  useEffect(() => {
    if (isMissing) onUnloadable?.();
  }, [isMissing, onUnloadable]);

  // No routine targeted (query disabled): render no card.
  if (routineId === undefined) return null;

  return (
    <CanvasModalCard
      title={routine?.name || "Routine"}
      icon={Zap}
      container={container}
      showBack={showBack}
      isFullscreen={isFullscreen}
      onBack={onBack}
      onClose={onClose}
    >
      <div className="px-4 py-4">
        {/* Leaving the routine means closing this layer. Its own default is a
            page navigation to the routines list, which from inside a thread
            would take the conversation with it. */}
        <RoutineDetail routineId={routineId} embedded onLeave={showBack ? onBack : onClose} />
      </div>
    </CanvasModalCard>
  );
};
